import { isMaintenanceResponse, reportMaintenance } from "./maintenance";

const sleep = (time: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, time));

// Only statuses the backend sends before doing any work (admission-shed 503,
// rate-limit 429), so retrying is safe even for mutations. Retrying any other
// status could double-execute a POST whose first attempt did run.
const RETRYABLE_STATUSES = new Set([429, 503]);

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 10_000;
const JITTER_MS = 300;

const retryDelayMs = (attempt: number, response?: Response): number => {
  // Retry-After arrives in whole seconds; a date-form or absent header falls
  // back to exponential backoff.
  const retryAfterSeconds = Number(response?.headers.get("Retry-After"));
  const retryAfterMs = Number.isFinite(retryAfterSeconds)
    ? retryAfterSeconds * 1000
    : 0;
  const backoffMs = BASE_DELAY_MS * 2 ** attempt;
  return (
    Math.min(Math.max(retryAfterMs, backoffMs), MAX_DELAY_MS) +
    Math.random() * JITTER_MS
  );
};

/**
 * A fetch wrapper that retries transient failures: network errors and 429/503
 * responses, honoring `Retry-After` with jittered exponential backoff. Any
 * other non-ok status throws immediately (no retry), carrying the response
 * body in the message — callers rely on ok-or-throw.
 */
export const fetchWithRetry = async (
  url: string,
  options: RequestInit = {},
  retries = 4,
): Promise<Response> => {
  for (let attempt = 0; ; attempt++) {
    let response: Response;
    try {
      response = await fetch(url, options);
    } catch (error: any) {
      if (error.name === "AbortError" || attempt >= retries) {
        throw error;
      }
      await sleep(retryDelayMs(attempt));
      continue;
    }

    if (response.ok) {
      return response;
    }

    // Maintenance lasts the length of an upgrade, so waiting it out here would only stall
    // the page that is about to say why it is down.
    if (isMaintenanceResponse(response)) {
      reportMaintenance();
      throw new Error("Layer is down for scheduled maintenance");
    }

    if (RETRYABLE_STATUSES.has(response.status) && attempt < retries) {
      await sleep(retryDelayMs(attempt, response));
      continue;
    }

    const body = await response.text().catch(() => "");
    throw new Error(
      `Fetch failed with status: ${response.status}${
        body ? ` — ${body.slice(0, 500)}` : ""
      }`,
    );
  }
};
